import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { ClientInvoicePaymentLink } from 'src/typings';
import { ClientInvoiceService } from '../client-invoice.service';

export interface ClientInvoicePaymentLinkDialogData {
  invoiceId: number;
  invoiceTitle: string;
  outstandingAmount: number;
}

@Component({
  selector: 'app-client-invoice-payment-link-dialog',
  templateUrl: './payment-link-dialog.component.html',
  styleUrls: ['./payment-link-dialog.component.scss']
})
export class PaymentLinkDialogComponent implements OnInit {
  loading = false;
  submitting = false;
  generatedLink?: ClientInvoicePaymentLink;
  links: ClientInvoicePaymentLink[] = [];
  availableAmount = 0;
  activeReservedAmount = 0;
  deletingById: Record<number, boolean> = {};

  form = this.fb.group({
    amount: new UntypedFormControl(null, [Validators.required, Validators.min(0.01)]),
    description: new UntypedFormControl('')
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ClientInvoicePaymentLinkDialogData,
    private dialogRef: MatDialogRef<PaymentLinkDialogComponent>,
    private fb: UntypedFormBuilder,
    private clientInvoiceService: ClientInvoiceService,
    private notificationService: NotificationService,
    private clipboard: Clipboard,
    public supportService: SupportService
  ) {}

  ngOnInit(): void {
    this.availableAmount = this.roundMoney(this.data.outstandingAmount || 0);
    this.setDefaultAmount();
    this.loadLinks();
  }

  get amountControl() {
    return this.form.controls.amount as UntypedFormControl;
  }

  get descriptionControl() {
    return this.form.controls.description as UntypedFormControl;
  }

  async loadLinks() {
    this.loading = true;
    try {
      this.links = await firstValueFrom(this.clientInvoiceService.getPaymentLinks(this.data.invoiceId));
      this.recalculateAvailableAmount();
      this.setDefaultAmount();
    } catch (error) {
      this.notificationService.warning('Не удалось загрузить LiqPay ссылки');
    } finally {
      this.loading = false;
    }
  }

  async submit() {
    if (this.submitting) return;
    const amount = this.roundMoney(Number(this.amountControl.value));
    if (!Number.isFinite(amount) || amount <= 0) {
      this.notificationService.warning('Сумма должна быть больше 0');
      return;
    }
    if (amount > this.availableAmount + 0.009) {
      this.notificationService.warning('Сумма больше доступного остатка счета');
      return;
    }

    this.submitting = true;
    try {
      const link = await firstValueFrom(
        this.clientInvoiceService.createPaymentLink(this.data.invoiceId, {
          amount,
          description: String(this.descriptionControl.value || '').trim() || undefined
        })
      );
      this.generatedLink = link;
      this.links = [link, ...this.links];
      this.recalculateAvailableAmount();
      this.copyGeneratedUrl();
      this.notificationService.notice('Ссылка LiqPay создана');
    } catch (error: any) {
      this.notificationService.warning(
        error?.error?.message || error?.message || 'Не удалось создать LiqPay ссылку'
      );
    } finally {
      this.submitting = false;
    }
  }

  close() {
    this.dialogRef.close(this.generatedLink || null);
  }

  copy(url?: string) {
    if (!url) return;
    if (this.clipboard.copy(url)) {
      this.notificationService.notice('Ссылка скопирована');
      return;
    }
    this.notificationService.warning('Ссылка доступна в поле ниже');
  }

  canDelete(link: ClientInvoicePaymentLink) {
    return link.status !== 'PAID' && link.status !== 'CANCELED' && !link.payment_id;
  }

  async deleteLink(link: ClientInvoicePaymentLink) {
    if (!this.canDelete(link) || this.deletingById[link.id]) return;
    if (!window.confirm('Удалить LiqPay ссылку?')) return;

    this.deletingById[link.id] = true;
    try {
      const updated = await firstValueFrom(
        this.clientInvoiceService.deletePaymentLink(this.data.invoiceId, link.id)
      );
      this.links = this.links.map((row) => (row.id === updated.id ? updated : row));
      if (this.generatedLink?.id === updated.id) {
        this.generatedLink = undefined;
      }
      this.recalculateAvailableAmount();
      this.setDefaultAmount();
      this.notificationService.notice('LiqPay ссылка удалена');
    } catch (error: any) {
      this.notificationService.warning(
        error?.error?.message || error?.message || 'Не удалось удалить LiqPay ссылку'
      );
    } finally {
      delete this.deletingById[link.id];
    }
  }

  getStatusLabel(status: ClientInvoicePaymentLink['status']) {
    switch (status) {
      case 'CREATED':
        return 'Ожидает оплаты';
      case 'PROCESSING':
        return 'Обрабатывается';
      case 'PAID':
        return 'Оплачено';
      case 'FAILED':
        return 'Ошибка';
      case 'EXPIRED':
        return 'Просрочена';
      case 'CANCELED':
        return 'Отменена';
      default:
        return status;
    }
  }

  getStatusClass(status: ClientInvoicePaymentLink['status']) {
    return `status-${String(status || '').toLowerCase()}`;
  }

  getFiscalizationStatusLabel(
    status?: ClientInvoicePaymentLink['fiscalization_status']
  ) {
    switch (status) {
      case 'NOT_ATTEMPTED':
        return 'Фискализация не запускалась';
      case 'PENDING':
        return 'Чек ожидает фискализации';
      case 'SUCCESS':
        return 'Чек фискализирован';
      case 'FAILED':
        return 'Фискализация остановлена';
      default:
        return 'Статус фискализации неизвестен';
    }
  }

  getFiscalizationStatusClass(
    status?: ClientInvoicePaymentLink['fiscalization_status']
  ) {
    return `fiscalization-${String(status || 'unknown').toLowerCase()}`;
  }

  private setDefaultAmount() {
    const current = Number(this.amountControl.value || 0);
    if (current > 0 && current <= this.availableAmount + 0.009) return;
    this.amountControl.setValue(this.availableAmount > 0 ? this.availableAmount : null);
  }

  private recalculateAvailableAmount() {
    const now = Date.now();
    this.activeReservedAmount = this.roundMoney(
      this.links
        .filter(
          (link) =>
            (link.status === 'CREATED' || link.status === 'PROCESSING') &&
            new Date(link.expires_at).getTime() > now
        )
        .reduce((sum, link) => sum + Number(link.amount || 0), 0)
    );
    this.availableAmount = this.roundMoney(
      Math.max(Number(this.data.outstandingAmount || 0) - this.activeReservedAmount, 0)
    );
  }

  private copyGeneratedUrl() {
    if (!this.generatedLink?.url) return;
    if (!this.clipboard.copy(this.generatedLink.url)) {
      this.notificationService.warning('Ссылка доступна в поле ниже');
    }
  }

  private roundMoney(value: number) {
    return Math.round((Number(value) || 0) * 100) / 100;
  }
}
